export default function ({ app, $config, route }) {
  if (process.client) {
    const getOneTrustId = (path) => {
      const routePath = (path || route.path).toLowerCase();
      if (routePath.includes("apsa")) {
        return $config.ONETRUST_APSA_ID;
      } else if (routePath.includes("chemrxiv")) {
        return $config.ONETRUST_CHEMRXIV_ID;
      } else {
        return $config.ONETRUST_ID;
      }
    };

    // Function to ensure OneTrust scripts are always at the top
    const ensureOneTrustAtTop = () => {
      const head = document.head || document.getElementsByTagName("head")[0];
      const autoBlockScript = document.querySelector("[data-onetrust-loaded]");
      const sdkScript = document.querySelector("[data-domain-script]");

      if (autoBlockScript && head.firstChild !== autoBlockScript) {
        head.insertBefore(autoBlockScript, head.firstChild);
      }
      if (sdkScript && head.children[1] !== sdkScript) {
        if (head.firstChild) {
          head.insertBefore(sdkScript, head.firstChild.nextSibling);
        } else {
          head.appendChild(sdkScript);
        }
      }
    };

    const checkUnleashAndInjectOneTrust = (currentPath) => {
      if (app.$unleash && app.$unleash.isEnabled("showOneTrustBanner")) {
        injectOneTrustScripts(currentPath);
      }
    };

    const injectOneTrustScripts = (currentPath) => {
      const newOneTrustId = getOneTrustId(currentPath);

      // Check if we need to update OneTrust for a different ID
      const existingScript = document.querySelector("[data-domain-script]");
      const existingId = existingScript
        ? existingScript.getAttribute("data-domain-script")
        : null;

      // If we have scripts with a different ID, remove them
      if (existingId && existingId !== newOneTrustId) {
        const oneTrustScripts = document.querySelectorAll(
          "[data-onetrust-loaded], [data-domain-script], script[src*='otSDKStub.js']"
        );
        oneTrustScripts.forEach((script) => script.remove());

        // Clear OneTrust globals to allow re-initialization
        delete window.OneTrust;
        delete window.OptanonWrapper;
        delete window.__ONETRUST_INJECTING__;
      }

      if (
        document.querySelector("[data-onetrust-loaded]") ||
        document.querySelector("[data-domain-script]") ||
        window.OneTrust ||
        window.OptanonWrapper ||
        window.__ONETRUST_INJECTING__
      ) {
        // If scripts exist, ensure they're at the top
        ensureOneTrustAtTop();
        return;
      }

      window.__ONETRUST_INJECTING__ = true;

      const insertAtHeadStart = (el) => {
        const head = document.head || document.getElementsByTagName("head")[0];
        // Insert at the very beginning, before any existing children
        if (head.firstChild) {
          head.insertBefore(el, head.firstChild);
        } else {
          head.appendChild(el);
        }
      };

      const oneTrustId = newOneTrustId;

      // Create all scripts first
      const autoBlockScript = document.createElement("script");
      autoBlockScript.type = "text/javascript";
      autoBlockScript.src = `${$config.ONETRUST_URL}/consent/${oneTrustId}/OtAutoBlock.js`;
      autoBlockScript.setAttribute("data-onetrust-loaded", "true");
      autoBlockScript.onload = () => {
        window.__ONETRUST_INJECTING__ = false;
      };
      autoBlockScript.onerror = () => {
        window.__ONETRUST_INJECTING__ = false;
      };

      const sdkStubScript = document.createElement("script");
      sdkStubScript.src = `${$config.ONETRUST_URL}/scripttemplates/otSDKStub.js`;
      sdkStubScript.type = "text/javascript";
      sdkStubScript.charset = "UTF-8";
      sdkStubScript.setAttribute("data-domain-script", oneTrustId);

      const wrapperScript = document.createElement("script");
      wrapperScript.type = "text/javascript";
      wrapperScript.textContent = `function OptanonWrapper() {
        // Wait for OneTrust to be fully loaded
        const setupConsentListener = () => {
          if (typeof window?.OneTrust?.OnConsentChanged === 'function') {
            window.OneTrust.OnConsentChanged(function () {
              console.log('OneTrust consent changed, reloading page...');
              window.location.reload();
            });
          } else {
            // Retry after a short delay if OneTrust isn't ready yet
            setTimeout(setupConsentListener, 100);
          }
        };
        
        // Setup the consent listener
        setupConsentListener();
      }`;

      // Insert in reverse so final order at top is: AutoBlock, SDK, Wrapper
      insertAtHeadStart(wrapperScript);
      insertAtHeadStart(sdkStubScript);
      insertAtHeadStart(autoBlockScript);

      // Set up periodic checks to ensure OneTrust stays at top
      const maintainTopPosition = setInterval(() => {
        ensureOneTrustAtTop();
      }, 1000);

      // Clear interval after 30 seconds to avoid indefinite polling
      setTimeout(() => {
        clearInterval(maintainTopPosition);
      }, 30000);
    };

    // Initial injection
    if (app.$unleash) {
      checkUnleashAndInjectOneTrust(route.path);
    } else {
      const checkInterval = setInterval(() => {
        if (app.$unleash) {
          clearInterval(checkInterval);
          checkUnleashAndInjectOneTrust(route.path);
        }
      }, 100);

      setTimeout(() => {
        clearInterval(checkInterval);
      }, 10000);
    }

    // Listen for route changes to ensure OneTrust scripts stay first
    app.router.afterEach((to, from) => {
      setTimeout(() => {
        checkUnleashAndInjectOneTrust(to.path);
        // Ensure OneTrust stays at top after route change
        ensureOneTrustAtTop();
      }, 50);
    });

    // Monitor for any DOM changes that might affect script order
    if (typeof MutationObserver !== "undefined") {
      const observer = new MutationObserver((mutations) => {
        let shouldCheck = false;
        mutations.forEach((mutation) => {
          if (
            mutation.type === "childList" &&
            mutation.target === document.head
          ) {
            shouldCheck = true;
          }
        });
        if (shouldCheck) {
          ensureOneTrustAtTop();
        }
      });

      // Start observing head for script additions
      observer.observe(document.head, {
        childList: true,
        subtree: false,
      });
    }
  }
}
